import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RuleIcon from '@mui/icons-material/Rule';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box, Chip, Collapse, Divider, Paper, Stack, TextField, Typography } from '@mui/material';

import ApprovalActionButton from './ApprovalActionButton';
import ApprovalStatusChip from './ApprovalStatusChip';
import ManualChip from './ManualChip';
import ToggleDetailsButton from './ToggleDetailsButton';

export type ChecklistRuleDataPoint = {
  error?: boolean;
  label: string;
  readOnly?: boolean;
  value: string;
  onChange?: (value: string) => void;
};

interface Props {
  approved: boolean;
  dataPoints: Array<ChecklistRuleDataPoint>;
  expanded: boolean;
  manuallyApproved: boolean;
  rule: string;
  summary: string;
  title: string;
  approvalNote?: string;
  blocked?: boolean;
  dataPointsLabel?: string;
  onApprovalChange?: () => void;
  onUnapprovalChange?: () => void;
  showApprovalNote?: boolean;
  onToggleExpanded: () => void;
}

export default function ChecklistRuleCard({
  approved,
  dataPoints,
  expanded,
  manuallyApproved,
  rule,
  summary,
  title,
  approvalNote,
  blocked = false,
  dataPointsLabel = 'Data points',
  onApprovalChange,
  onUnapprovalChange,
  showApprovalNote = false,
  onToggleExpanded,
}: Props) {
  const showNote = showApprovalNote && !!approvalNote;
  const statusColor = blocked ? 'grey.500' : approved ? 'success.main' : 'warning.main';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: blocked ? 'grey.300' : approved ? 'success.light' : 'warning.light',
        borderRadius: 1.25,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        sx={{
          bgcolor: blocked ? 'grey.50' : approved ? 'success.50' : 'warning.50',
          px: 2.5,
          py: 2.25,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ color: statusColor, display: 'flex', pt: 0.25 }}>
            {blocked ? <ErrorOutlineIcon /> : approved ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              {blocked ? (
                <Chip color="default" label="Missing data" size="small" variant="outlined" />
              ) : (
                <ApprovalStatusChip approved={approved} />
              )}
              {approved && manuallyApproved && <ManualChip />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {summary}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={{ xs: 'flex-end', md: 'flex-start' }}>
          <ToggleDetailsButton expanded={expanded} onClick={onToggleExpanded} />
          <ApprovalActionButton
            approved={approved}
            hidden={blocked}
            onApprove={onApprovalChange}
            onUnapprove={onUnapprovalChange}
          />
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Stack spacing={2.25} sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RuleIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>
                Rule
              </Typography>
            </Stack>
            <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2">{rule}</Typography>
            </Paper>
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TableChartIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>
                {dataPointsLabel}
              </Typography>
            </Stack>
            <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
              {dataPoints.map((dataPoint, index) => {
                const editable = isDataPointEditable(dataPoint);

                return (
                  <Box key={dataPoint.label}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      sx={{ px: 2, py: 1.25 }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 240px', fontWeight: 500 }}>
                        {dataPoint.label}
                      </Typography>
                      {editable ? (
                        <TextField
                          error={dataPoint.error}
                          onChange={(event) => dataPoint.onChange?.(event.target.value)}
                          size="small"
                          value={dataPoint.value}
                          InputProps={{
                            sx: dataPoint.error ? { color: 'error.main' } : undefined,
                          }}
                          inputProps={{ 'aria-invalid': dataPoint.error }}
                          fullWidth
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color={dataPoint.error ? 'error.main' : 'text.primary'}
                          sx={{ py: 1, flex: 1 }}
                        >
                          {dataPoint.value}
                        </Typography>
                      )}
                    </Stack>
                    {index < dataPoints.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Paper>
          </Stack>

          {showNote && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PriorityHighIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Approval note
                </Typography>
              </Stack>
              <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
                <Typography variant="body2">{approvalNote}</Typography>
              </Paper>
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}

function isDataPointEditable(dataPoint: ChecklistRuleDataPoint) {
  if (dataPoint.readOnly != null) {
    return !dataPoint.readOnly;
  }

  return !!dataPoint.onChange;
}
